import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { StatusPill } from "../components/StatusPill";
import { HeroDiagram } from "../components/HeroDiagram";

const principles = [
  {
    code: "01",
    title: "Purpose-bound exchange",
    body: "Information moves for a stated purpose, with a receipt that proves the purpose was honoured. Nothing more is shared by default.",
  },
  {
    code: "02",
    title: "Attributable authority",
    body: "Every action carries who was authorised, under what mandate. Authority can be re-checked, not merely assumed.",
  },
  {
    code: "03",
    title: "Signed receipts",
    body: "Requests, approvals, actions, and handoffs produce cryptographic receipts that survive the workflow itself.",
  },
  {
    code: "04",
    title: "Selective disclosure",
    body: "Visitors keep custody of their own working material. KATLAS does not become a default vault.",
  },
];

const CLUBCIERGE_URL = "https://clubcierge.katlastechnology.com/";

export const Landing = () => {
  return (
    <div data-testid="landing-page" className="bg-white">
      {/* HERO — dark navy panel, KATLAS-aligned */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-border text-white"
        style={{
          background:
            "linear-gradient(180deg, #061A2F 0%, #0A2540 55%, #0A2540 100%)",
        }}
      >
        {/* faint plane lines */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "100% 96px",
          }}
        />
        <div className="relative mx-auto grid max-w-[1280px] gap-12 px-6 py-20 md:grid-cols-12 md:py-28 lg:px-10 lg:py-32">
          <div className="md:col-span-7">
            <p
              data-testid="hero-eyebrow"
              className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#7FB2FF]"
            >
              Cierge Labs · A KATLAS experience
            </p>
            <h1
              data-testid="hero-headline"
              className="mt-6 font-serif text-5xl font-light leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-[80px]"
            >
              A guided design environment for{" "}
              <span className="italic text-[#C19A4A]">governed</span>{" "}
              coordination.
            </h1>
            <p
              data-testid="hero-sub"
              className="mt-8 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg"
            >
              Cierge Labs helps organisations shape candidate use cases above
              the KATLAS governance layer — moving from a coordination problem
              to a bounded, receipt-backed proof. Keep custody of your inputs,
              map authority, and progress to a relevant demo or private
              sandbox.
            </p>

            <div className="mt-8">
              <StatusPill tone="dark" testid="hero-status-pill" />
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                asChild
                size="lg"
                data-testid="hero-cta-explore"
                className="rounded-none bg-white px-7 font-mono text-xs uppercase tracking-[0.18em] text-[#0A2540] hover:bg-[#E6F0FF]"
              >
                <Link to="/explore">Shape a use case</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="hero-cta-clubcierge"
                className="rounded-none border-white/30 bg-transparent px-7 font-mono text-xs uppercase tracking-[0.18em] text-white hover:border-white hover:bg-white/5 hover:text-white"
              >
                <a
                  href={CLUBCIERGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See live ClubCierge proof →
                </a>
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">
              <span data-testid="hero-tag-synthetic">› Synthetic by default</span>
              <span data-testid="hero-tag-bounded">› Bounded · not a copilot</span>
              <span data-testid="hero-tag-custody">› Self-sovereign continuity</span>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="h-full min-h-[480px] w-full">
              <HeroDiagram />
            </div>
          </div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section
        data-testid="proof-strip"
        className="border-b border-black/10 bg-[#061A2F] text-white"
      >
        <div className="mx-auto flex max-w-[1280px] flex-col items-start gap-6 px-6 py-7 md:flex-row md:items-center md:justify-between lg:px-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#7FB2FF]">
            <span className="text-white">Custody</span>{" "}
            <span className="text-white/40">·</span>{" "}
            <span className="text-white">Authority</span>{" "}
            <span className="text-white/40">·</span>{" "}
            <span className="text-white">Receipts</span>
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/65">
            Synthetic by default
            <span className="mx-3 text-white/30">·</span>
            Live KATLAS node
            <span className="mx-3 text-white/30">·</span>
            Progress to private sandbox
          </p>
        </div>
      </section>

      {/* CAR PATTERN — verbatim positioning lifted from platform.katlastechnology.com */}
      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-[1280px] px-6 py-20 md:py-24 lg:px-10">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#0A2540]/70">
                The CAR Pattern
              </p>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-[#0A2540]">
                Custody. Authority. Receipts.
              </h2>
            </div>
            <div className="md:col-span-8 space-y-6 text-base leading-relaxed text-foreground/90">
              <p>
                <span className="font-medium text-[#0A2540]">
                  Custody controls the evidence. Authority governs the action.
                  Receipts prove what happened.
                </span>
              </p>
              <p className="border-l-2 border-[#0A2540]/40 pl-5 text-foreground/80">
                KATLAS records the governed action, not a copy of everyone's
                private data.
              </p>
              <div className="grid gap-px bg-border sm:grid-cols-3">
                {[
                  {
                    glyph: "C",
                    title: "Custody",
                    body:
                      "Who controls the relevant wallet, profile, asset, entitlement or evidence — held at source.",
                  },
                  {
                    glyph: "A",
                    title: "Authority",
                    body:
                      "Who is allowed to request, approve, refuse, share, claim, hand off or escalate — under explicit policy.",
                  },
                  {
                    glyph: "R",
                    title: "Receipts",
                    body:
                      "What proves the governed action happened — hash, signer, timestamp, shared refs and withheld counts.",
                  },
                ].map((c) => (
                  <article
                    key={c.glyph}
                    data-testid={`car-${c.title.toLowerCase()}`}
                    className="bg-white p-6"
                  >
                    <div className="flex items-baseline gap-3">
                      <span className="font-serif text-3xl font-light text-[#0A2540]">
                        {c.glyph}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#0A2540]/70">
                        {c.title}
                      </span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {c.body}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT */}
      <section className="border-b border-border bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1280px] px-6 py-20 md:py-24 lg:px-10">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Section · 01 / What
              </p>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight">
                What governed coordination means here.
              </h2>
            </div>
            <div className="md:col-span-8">
              <div className="space-y-6 text-base leading-relaxed text-foreground/90">
                <p>
                  KATLAS is an operational governance layer for accountable
                  coordination across people, systems, and AI. It enables
                  purpose-bound exchange, attributable authority, and signed
                  receipts for requests, approvals, actions, and handoffs —
                  without requiring centralisation of sensitive data.
                </p>
                <p>
                  Cierge Labs is the bounded, guided design environment above
                  it. You are not modelling production data here. You are
                  shaping a contract: who can act, on what authority, with
                  what receipt, and what is shared selectively rather than
                  centralised.
                </p>
                <p className="border-l-2 border-[#0A2540] pl-5 font-mono text-sm uppercase tracking-[0.12em] text-[#0A2540]">
                  The platform explains it. Cierge Labs proves it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-20 md:py-24 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Section · 02 / Principles
              </p>
              <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight">
                The four governance primitives carried through every flow.
              </h2>
            </div>
          </div>
          <Separator className="my-10" />
          <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <article
                key={p.code}
                data-testid={`principle-${p.code}`}
                className="bg-white p-8 transition-colors hover:bg-[#FAFAFA]"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#0A2540]">
                  {p.code}
                </p>
                <h3 className="mt-4 font-serif text-2xl font-light tracking-tight text-foreground">
                  {p.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {p.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="border-b border-border bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1280px] px-6 py-20 md:py-24 lg:px-10">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Section · 03 / Journey
              </p>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight">
                A short, guided path. Five steps. No open-ended chat.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                You select your role, your sector, the coordination problem,
                and what specifically needs governing. Cierge Labs returns a
                tailored explainer, a synthetic case, and a recommended next
                step. You can optionally hold the result in a wallet that
                remains in your browser.
              </p>
              <Button
                asChild
                className="mt-8 rounded-none px-6 font-mono text-xs uppercase tracking-[0.18em]"
                data-testid="journey-cta"
              >
                <Link to="/explore">Begin the guided flow</Link>
              </Button>
            </div>
            <ol className="space-y-px bg-border md:col-span-7">
              {[
                ["01", "Select role", "Buyer, operator, partner, or assurance lead."],
                ["02", "Select sector", "Health, supply chain, mobility, cities & resilience."],
                ["03", "Select problem", "Approvals, exceptions, handoffs, AI decisions, evidence."],
                ["04", "Select scope", "What specifically needs governing inside the workflow."],
                ["05", "Tailored output", "Explainer, synthetic case, and recommended next step."],
              ].map(([code, title, body]) => (
                <li
                  key={code}
                  className="grid grid-cols-[80px_1fr] gap-6 bg-white p-7"
                >
                  <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                    Step {code}
                  </span>
                  <div>
                    <p className="font-serif text-xl font-light tracking-tight text-foreground">
                      {title}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* COMMERCIAL PATH */}
      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-[1280px] px-6 py-20 md:py-24 lg:px-10">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Section · 04 / Path
              </p>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight">
                Free to explore. Paid where it earns its keep.
              </h2>
            </div>
            <div className="grid gap-px bg-border md:col-span-8 md:grid-cols-3">
              {[
                {
                  code: "free",
                  label: "Guided exploration",
                  body: "Shape a use case, retain it in your wallet, and walk a public synthetic demo. No commercial commitment.",
                  cta: { to: "/explore", label: "Start" },
                  testid: "path-free",
                },
                {
                  code: "sandbox",
                  label: "Private sandbox",
                  body: "A bounded white-label environment seeded with synthetic data shaped to your sector and problem.",
                  cta: { to: "/sandbox-request", label: "Request" },
                  testid: "path-sandbox",
                },
                {
                  code: "pilot",
                  label: "Pilot scoping",
                  body: "A short, structured conversation to define a candidate pilot and the evidence it would produce.",
                  cta: { to: "/sandbox-request?intent=pilot", label: "Open scoping" },
                  testid: "path-pilot",
                },
              ].map((p) => (
                <article key={p.code} data-testid={p.testid} className="bg-white p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {p.code}
                  </p>
                  <h3 className="mt-4 font-serif text-2xl font-light tracking-tight">
                    {p.label}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {p.body}
                  </p>
                  <Link
                    to={p.cta.to}
                    className="mt-8 inline-flex items-center gap-2 border-b border-foreground/30 pb-1 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground transition-colors hover:border-foreground"
                  >
                    {p.cta.label} →
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
